import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#020A0D",
      dark: "#920123",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#666666",
      dark: "#920123",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#1E60BA",
      light: "#DBE5F4",
    },
    text: {
      primary: "#333333",
      secondary: "#020A0D",
      hint: "#808890",
    },

    background: "#FFFFFF",
    backgroundHeader: "#F3F5F6",
    shade3: "#CCCCCC",
    shade6: "#333333",
    shade1: "#F5F4F3",
    border: "#8A8A8A",
    hover: "#5C5C5C",
    placeholder: "#999999",
    transparent: "#00000000",
    divider: "#E5E6E6",
  },
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const nissan = {
  themeName: "Nissan",
  brand: BrandsEnum.Nissan,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "2px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.divider,
      paper: variables.colors.divider,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Nissan"),
    color: variables.colors.text.primary,
    fontWeight: 500,
    h1: {
      fontFamily: getFontFamily("Nissan"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 700,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontFamily: getFontFamily("Nissan"),
      fontSize: "1.5rem",
      lineHeight: "2rem",
      color: variables.colors.text.secondary,
      fontWeight: 700,
      textAlign: "center" as "center",
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontFamily: getFontFamily("Nissan"),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 700,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily("Nissan"),
      color: variables.colors.text.secondary,
      fontWeight: 700,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily("Nissan"),
      fontSize: "1.25rem",
      color: variables.colors.primary.main,
      fontWeight: 700,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
      fontWeight: 500,
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      minHeight: 48,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: 500,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
      fontWeight: 500,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: "#FFFFFF",
    activeBorder: `2px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `2px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: variables.colors.transparent,
    },
  },
  header: {
    backgroundColor: variables.colors.background,
    "& h2": {
      fontSize: 40,
      lineHeight: "48px",
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
        borderRadius: 24,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 24,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
        fontWeight: 700,
        "a&": {
          borderRadius: 24,
          fontWeight: 700,
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.hover,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.hover,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "none",
        },
      },
      containedPrimary: {
        fontWeight: 700,
        "&:hover": {
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.hover,
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.hover,
          color: variables.colors.hover,
          backgroundColor: variables.colors.background,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "2px solid " + variables.colors.hover,
          color: variables.colors.hover,
          backgroundColor: variables.colors.background,
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.background,
        border: "2px solid " + variables.colors.primary.main,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          "&:hover": {
            border: "2px solid " + variables.colors.hover,
            color: variables.colors.hover,
            backgroundColor: variables.colors.background,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "2px solid " + variables.colors.hover,
            color: variables.colors.hover,
            backgroundColor: variables.colors.background,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.background,
          border: "2px solid " + variables.colors.primary.main,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          border: "2px solid " + variables.colors.text.hint,
          color: variables.colors.text.hint,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.text.primary,
        border: "2px solid " + variables.colors.text.primary,
      },
      text: {
        "&:hover": {
          color: variables.colors.hover,
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.primary.main,
        fontWeight: 700,
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.hover,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: getFontFamily("Nissan"),
        color: variables.colors.shade6,
        "&$focused": {
          color: variables.colors.border,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        fontFamily: getFontFamily("Nissan"),
        color: variables.colors.shade6,
        position: "relative" as PositionProperty,
        transform: "none",
        lineHeight: 1.75, // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        border: "2px solid " + variables.colors.border,
        backgroundColor: variables.colors.background,
        color: variables.colors.shade6,
        "&:hover": {
          color: variables.colors.primary.main,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.primary,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.shade1,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          fontWeight: 700,
          "&:hover": {
            backgroundColor: variables.colors.shade1,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.hover,
          textDecoration: "underline",
          "&:hover": {
            color: variables.colors.primary.main,
          },
          "&:active": {
            color: variables.colors.primary.main,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: 24,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: 24,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.shade6,
        borderRadius: 24,
        border: "2px solid " + variables.colors.border,
        "&$selected": {
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.background,
          borderColor: variables.colors.primary.main,
          "&:hover": {
            color: variables.colors.hover,
            backgroundColor: variables.colors.transparent,
          },
        },
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: variables.colors.shade6,
    textColor: variables.colors.secondary.contrastText,
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Nissan"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#101820",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Nissan"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  priceText: {
    totalPriceColor: variables.colors.primary.main,
    fontWeight: 700,
  },
  icons: {
    removeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 11.3305L19.1632 4.16736C19.2524 4.0781 19.3584 4.02789 19.4812 4.01674C19.6039 4.00558 19.7211 4.05579 19.8326 4.16736C19.9442 4.27894 20 4.39052 20 4.50209C20 4.61367 19.9442 4.72524 19.8326 4.83682L12.6695 12L19.8326 19.1632C19.9219 19.2524 19.9721 19.3584 19.9833 19.4812C19.9944 19.6039 19.9442 19.7211 19.8326 19.8326C19.7211 19.9442 19.6095 20 19.4979 20C19.3863 20 19.2748 19.9442 19.1632 19.8326L12 12.6695L4.83682 19.8326C4.74756 19.9219 4.64156 19.9721 4.51883 19.9833C4.3961 19.9944 4.27894 19.9442 4.16736 19.8326C4.05579 19.7211 4 19.6095 4 19.4979C4 19.3863 4.05579 19.2748 4.16736 19.1632L11.3305 12L4.16736 4.83682C4.0781 4.74756 4.02789 4.64156 4.01674 4.51883C4.00558 4.3961 4.05579 4.27894 4.16736 4.16737C4.27894 4.05579 4.39051 4 4.50209 4C4.61367 4 4.72524 4.05579 4.83682 4.16737L12 11.3305Z'
          fill='#C3002F'
        />
      </svg>
    ),
    infoIcon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M10.2785 14.8755C10.2043 14.9501 10.1121 14.9875 10.001 14.9875C9.88987 14.9875 9.79731 14.9501 9.72248 14.8755C9.64764 14.8008 9.6102 14.7085 9.6102 14.5977V9.25287C9.6102 9.14201 9.64733 9.04969 9.72151 8.97505C9.79566 8.90043 9.88786 8.86307 9.99899 8.86307C10.1101 8.86307 10.2027 8.90043 10.2775 8.97509C10.3524 9.04974 10.3898 9.14205 10.3898 9.25287V14.5977C10.3898 14.7086 10.3527 14.8009 10.2785 14.8755ZM10.4006 7.23972C10.293 7.34742 10.1597 7.40129 10 7.40129C9.84031 7.40129 9.70705 7.34742 9.59936 7.23972C9.49166 7.13203 9.43779 6.99877 9.43779 6.83908C9.43779 6.67939 9.49166 6.54613 9.59936 6.43844C9.70705 6.33074 9.84031 6.27687 10 6.27687C10.1597 6.27687 10.293 6.33074 10.4006 6.43844C10.5083 6.54613 10.5622 6.67939 10.5622 6.83908C10.5622 6.99877 10.5083 7.13203 10.4006 7.23972ZM10.005 19.9875C8.62398 19.9875 7.32556 19.7255 6.1096 19.2014C4.89339 18.6773 3.83557 17.966 2.93605 17.0676C2.03654 16.1692 1.32438 15.1128 0.799578 13.8981C0.274882 12.6837 0.0125 11.386 0.0125 10.005C0.0125 8.62398 0.274548 7.32556 0.798577 6.1096C1.32273 4.89339 2.034 3.83558 2.93238 2.93607C3.83078 2.03655 4.88725 1.32438 6.10191 0.799579C7.31633 0.274883 8.61399 0.0125 9.995 0.0125C11.376 0.0125 12.6744 0.274547 13.8904 0.798577C15.1066 1.32273 16.1644 2.034 17.0639 2.93238C17.9635 3.83078 18.6756 4.88725 19.2004 6.10191C19.7251 7.31633 19.9875 8.61399 19.9875 9.995C19.9875 11.376 19.7255 12.6744 19.2014 13.8904C18.6773 15.1066 17.966 16.1644 17.0676 17.064C16.1692 17.9635 15.1128 18.6756 13.8981 19.2004C12.6837 19.7251 11.386 19.9875 10.005 19.9875ZM10 19.2079C12.5703 19.2079 14.7478 18.3158 16.5318 16.5318C18.3158 14.7478 19.2079 12.5703 19.2079 10C19.2079 7.4297 18.3158 5.25218 16.5318 3.46817C14.7478 1.68417 12.5703 0.792098 10 0.792098C7.4297 0.792098 5.25218 1.68417 3.46817 3.46817C1.68417 5.25218 0.792098 7.4297 0.792098 10C0.792098 12.5703 1.68417 14.7478 3.46817 16.5318C5.25218 18.3158 7.4297 19.2079 10 19.2079Z'
          fill='#1E60BA'
          stroke='#1E60BA'
          stroke-width='0.025'
        />
      </svg>
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M12 12.6695L4.83682 19.8326C4.74756 19.9219 4.64156 19.9721 4.51883 19.9833C4.39609 19.9944 4.27894 19.9442 4.16736 19.8326C4.05579 19.7211 4 19.6095 4 19.4979C4 19.3863 4.05579 19.2748 4.16736 19.1632L11.3305 12L4.16736 4.83682C4.0781 4.74756 4.02789 4.64156 4.01674 4.51883C4.00558 4.39609 4.05579 4.27894 4.16736 4.16736C4.27894 4.05579 4.39052 4 4.50209 4C4.61367 4 4.72524 4.05579 4.83682 4.16736L12 11.3305L19.1632 4.16736C19.2524 4.0781 19.3584 4.02789 19.4812 4.01674C19.6039 4.00558 19.7211 4.05579 19.8326 4.16736C19.9442 4.27894 20 4.39052 20 4.50209C20 4.61367 19.9442 4.72524 19.8326 4.83682L12.6695 12L19.8326 19.1632C19.9219 19.2524 19.9721 19.3584 19.9833 19.4812C19.9944 19.6039 19.9442 19.7211 19.8326 19.8326C19.7211 19.9442 19.6095 20 19.4979 20C19.3863 20 19.2748 19.9442 19.1632 19.8326L12 12.6695Z'
            fill='#C3002F'
          />
        </g>
      </svg>
    ),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M10.3245 15.1123L10.3333 15.1211L10.3422 15.1123L16.6065 8.84792C16.701 8.7535 16.7938 8.7079 16.8851 8.7079C16.9764 8.7079 17.0692 8.7535 17.1636 8.84792C17.2575 8.94188 17.2984 9.03899 17.2893 9.13967C17.28 9.24205 17.2382 9.33034 17.1636 9.40495L10.9279 15.6406C10.7577 15.8108 10.5597 15.8955 10.3333 15.8955C10.107 15.8955 9.90895 15.8108 9.73872 15.6406L6.77895 12.6808C6.68453 12.5864 6.63894 12.4936 6.63894 12.4023C6.63894 12.311 6.68453 12.2182 6.77895 12.1238C6.87291 12.0298 6.97003 11.9889 7.07071 11.9981C7.17308 12.0074 7.26137 12.0492 7.33599 12.1238L10.3245 15.1123ZM12.005 21.9875C10.624 21.9875 9.32556 21.7255 8.1096 21.2014C6.89339 20.6773 5.83557 19.966 4.93605 19.0676C4.03654 18.1692 3.32438 17.1128 2.79958 15.8981C2.27488 14.6837 2.0125 13.386 2.0125 12.005C2.0125 10.624 2.27455 9.32556 2.79858 8.1096C3.32273 6.89339 4.034 5.83558 4.93238 4.93607C5.83078 4.03655 6.88725 3.32438 8.10191 2.79958C9.31633 2.27488 10.614 2.0125 11.995 2.0125C13.376 2.0125 14.6744 2.27455 15.8904 2.79858C17.1066 3.32273 18.1644 4.034 19.0639 4.93238C19.9635 5.83078 20.6756 6.88725 21.2004 8.10191C21.7251 9.31633 21.9875 10.614 21.9875 11.995C21.9875 13.376 21.7255 14.6744 21.2014 15.8904C20.6773 17.1066 19.966 18.1644 19.0676 19.064C18.1692 19.9635 17.1128 20.6756 15.8981 21.2004C14.6837 21.7251 13.386 21.9875 12.005 21.9875ZM12 21.2079C14.5703 21.2079 16.7478 20.3158 18.5318 18.5318C20.3158 16.7478 21.2079 14.5703 21.2079 12C21.2079 9.4297 20.3158 7.25218 18.5318 5.46817C16.7478 3.68417 14.5703 2.7921 12 2.7921C9.4297 2.7921 7.25218 3.68417 5.46817 5.46817C3.68417 7.25218 2.7921 9.4297 2.7921 12C2.7921 14.5703 3.68417 16.7478 5.46817 18.5318C7.25218 20.3158 9.4297 21.2079 12 21.2079Z'
          fill='#C3002F'
          stroke='#C3002F'
          stroke-width='0.025'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M10.001 15C10.1153 15 10.2107 14.9614 10.2874 14.8843C10.364 14.8072 10.4023 14.7117 10.4023 14.5977V9.25287C10.4023 9.13889 10.3636 9.04334 10.2864 8.96624C10.2091 8.88913 10.1133 8.85057 9.99899 8.85057C9.88472 8.85057 9.78927 8.88913 9.71264 8.96624C9.63601 9.04334 9.5977 9.13889 9.5977 9.25287V14.5977C9.5977 14.7117 9.63635 14.8072 9.71365 14.8843C9.79095 14.9614 9.88673 15 10.001 15ZM10 7.41379C10.1628 7.41379 10.2993 7.35872 10.4095 7.24856C10.5196 7.13841 10.5747 7.00192 10.5747 6.83908C10.5747 6.67625 10.5196 6.53975 10.4095 6.4296C10.2993 6.31944 10.1628 6.26437 10 6.26437C9.83716 6.26437 9.70067 6.31944 9.59052 6.4296C9.48036 6.53975 9.42529 6.67625 9.42529 6.83908C9.42529 7.00192 9.48036 7.13841 9.59052 7.24856C9.70067 7.35872 9.83716 7.41379 10 7.41379ZM10.005 20C8.62236 20 7.32224 19.7376 6.10465 19.2129C4.88705 18.6882 3.8279 17.976 2.92721 17.0765C2.02654 16.1769 1.31351 15.1191 0.788103 13.903C0.262701 12.687 0 11.3876 0 10.005C0 8.62236 0.262366 7.32224 0.787098 6.10466C1.31185 4.88705 2.02399 3.82791 2.92353 2.92724C3.82309 2.02655 4.8809 1.31351 6.09695 0.788104C7.31301 0.262702 8.61236 0 9.995 0C11.3776 0 12.6778 0.262366 13.8953 0.787097C15.1129 1.31185 16.1721 2.02399 17.0728 2.92353C17.9734 3.82309 18.6865 4.8809 19.2119 6.09695C19.7373 7.31301 20 8.61236 20 9.995C20 11.3776 19.7376 12.6778 19.2129 13.8953C18.6882 15.113 17.976 16.1721 17.0765 17.0728C16.1769 17.9735 15.1191 18.6865 13.903 19.2119C12.687 19.7373 11.3876 20 10.005 20ZM10 19.1954C12.567 19.1954 14.7414 18.3046 16.523 16.523C18.3046 14.7414 19.1954 12.567 19.1954 10C19.1954 7.43295 18.3046 5.25862 16.523 3.47701C14.7414 1.6954 12.567 0.804598 10 0.804598C7.43295 0.804598 5.25862 1.6954 3.47701 3.47701C1.6954 5.25862 0.804598 7.43295 0.804598 10C0.804598 12.567 1.6954 14.7414 3.47701 16.523C5.25862 18.3046 7.43295 19.1954 10 19.1954Z'
          fill='#C3002F'
        />
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M12 12.6695L4.83682 19.8326C4.74756 19.9219 4.64156 19.9721 4.51883 19.9833C4.39609 19.9944 4.27894 19.9442 4.16736 19.8326C4.05579 19.7211 4 19.6095 4 19.4979C4 19.3863 4.05579 19.2748 4.16736 19.1632L11.3305 12L4.16736 4.83682C4.0781 4.74756 4.02789 4.64156 4.01674 4.51883C4.00558 4.39609 4.05579 4.27894 4.16736 4.16736C4.27894 4.05579 4.39052 4 4.50209 4C4.61367 4 4.72524 4.05579 4.83682 4.16736L12 11.3305L19.1632 4.16736C19.2524 4.0781 19.3584 4.02789 19.4812 4.01674C19.6039 4.00558 19.7211 4.05579 19.8326 4.16736C19.9442 4.27894 20 4.39052 20 4.50209C20 4.61367 19.9442 4.72524 19.8326 4.83682L12.6695 12L19.8326 19.1632C19.9219 19.2524 19.9721 19.3584 19.9833 19.4812C19.9944 19.6039 19.9442 19.7211 19.8326 19.8326C19.7211 19.9442 19.6095 20 19.4979 20C19.3863 20 19.2748 19.9442 19.1632 19.8326L12 12.6695Z'
            fill='#020A0D'
          />
        </g>
      </svg>
    ),
    addIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.4731 12.4734L12.4731 22.6036C12.4731 22.7299 12.4337 22.8403 12.3548 22.935C12.2759 23.0297 12.1575 23.077 11.9997 23.077C11.842 23.077 11.7236 23.0376 11.6447 22.9587C11.5658 22.8798 11.5264 22.7614 11.5264 22.6036L11.5264 12.4734L1.3961 12.4734C1.26987 12.4734 1.15941 12.4339 1.06474 12.355C0.970061 12.2761 0.922724 12.1578 0.922724 12C0.922724 11.8422 0.962172 11.7239 1.04107 11.645C1.11996 11.5661 1.23831 11.5266 1.3961 11.5266L11.5264 11.5266L11.5264 1.39636C11.5264 1.27012 11.5658 1.15967 11.6447 1.06499C11.7236 0.970318 11.842 0.92298 11.9997 0.92298C12.1575 0.92298 12.2759 0.962428 12.3548 1.04132C12.4337 1.12022 12.4731 1.23856 12.4731 1.39636L12.4731 11.5266L22.6034 11.5266C22.7296 11.5266 22.8401 11.5661 22.9347 11.645C23.0294 11.7239 23.0768 11.8422 23.0768 12C23.0768 12.1578 23.0373 12.2761 22.9584 12.355C22.8795 12.4339 22.7612 12.4734 22.6034 12.4734L12.4731 12.4734Z'
          fill='#C3002F'
        />
      </svg>
    ),
    selectIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 15.0282L19.0734 7.9548C19.1789 7.84934 19.307 7.79661 19.4576 7.79661C19.6083 7.79661 19.7363 7.84934 19.8418 7.9548C19.9473 8.06026 20 8.18832 20 8.33898C20 8.48964 19.9473 8.6177 19.8418 8.72316L12.9492 15.6158C12.8136 15.7514 12.6629 15.8493 12.4972 15.9096C12.3315 15.9699 12.1657 16 12 16C11.8343 16 11.6686 15.9699 11.5028 15.9096C11.3371 15.8493 11.1864 15.7514 11.0508 15.6158L4.15819 8.72316C4.05273 8.6177 4 8.48964 4 8.33898C4 8.18832 4.05273 8.06026 4.15819 7.9548C4.26365 7.84934 4.39171 7.79661 4.54237 7.79661C4.69303 7.79661 4.82109 7.84934 4.92655 7.9548L12 15.0282Z'
          fill='#C3002F'
          stroke='#C3002F'
        />
      </svg>
    ),
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#000' stroke='#000' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <svg width='34' height='13' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M10.3245 15.1123L10.3333 15.1211L10.3422 15.1123L16.6065 8.84792C16.701 8.7535 16.7938 8.7079 16.8851 8.7079C16.9764 8.7079 17.0692 8.7535 17.1636 8.84792C17.2575 8.94188 17.2984 9.03899 17.2893 9.13967C17.28 9.24205 17.2382 9.33034 17.1636 9.40495L10.9279 15.6406C10.7577 15.8108 10.5597 15.8955 10.3333 15.8955C10.107 15.8955 9.90895 15.8108 9.73872 15.6406L6.77895 12.6808C6.68453 12.5864 6.63894 12.4936 6.63894 12.4023C6.63894 12.311 6.68453 12.2182 6.77895 12.1238C6.87291 12.0298 6.97003 11.9889 7.07071 11.9981C7.17308 12.0074 7.26137 12.0492 7.33599 12.1238L10.3245 15.1123ZM12.005 21.9875C10.624 21.9875 9.32556 21.7255 8.1096 21.2014C6.89339 20.6773 5.83557 19.966 4.93605 19.0676C4.03654 18.1692 3.32438 17.1128 2.79958 15.8981C2.27488 14.6837 2.0125 13.386 2.0125 12.005C2.0125 10.624 2.27455 9.32556 2.79858 8.1096C3.32273 6.89339 4.034 5.83558 4.93238 4.93607C5.83078 4.03655 6.88725 3.32438 8.10191 2.79958C9.31633 2.27488 10.614 2.0125 11.995 2.0125C13.376 2.0125 14.6744 2.27455 15.8904 2.79858C17.1066 3.32273 18.1644 4.034 19.0639 4.93238C19.9635 5.83078 20.6756 6.88725 21.2004 8.10191C21.7251 9.31633 21.9875 10.614 21.9875 11.995C21.9875 13.376 21.7255 14.6744 21.2014 15.8904C20.6773 17.1066 19.966 18.1644 19.0676 19.064C18.1692 19.9635 17.1128 20.6756 15.8981 21.2004C14.6837 21.7251 13.386 21.9875 12.005 21.9875ZM12 21.2079C14.5703 21.2079 16.7478 20.3158 18.5318 18.5318C20.3158 16.7478 21.2079 14.5703 21.2079 12C21.2079 9.4297 20.3158 7.25218 18.5318 5.46817C16.7478 3.68417 14.5703 2.7921 12 2.7921C9.4297 2.7921 7.25218 3.68417 5.46817 5.46817C3.68417 7.25218 2.7921 9.4297 2.7921 12C2.7921 14.5703 3.68417 16.7478 5.46817 18.5318C7.25218 20.3158 9.4297 21.2079 12 21.2079Z'
            fill='#F5F4F3'
            stroke='#F5F4F3'
            stroke-width='0.025'
          />
        </svg>
      </>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.3245 15.1123L10.3333 15.1211L10.3422 15.1123L16.6065 8.84792C16.701 8.7535 16.7938 8.7079 16.8851 8.7079C16.9764 8.7079 17.0692 8.7535 17.1636 8.84792C17.2575 8.94188 17.2984 9.03899 17.2893 9.13967C17.28 9.24205 17.2382 9.33034 17.1636 9.40495L10.9279 15.6406C10.7577 15.8108 10.5597 15.8955 10.3333 15.8955C10.107 15.8955 9.90895 15.8108 9.73872 15.6406L6.77895 12.6808C6.68453 12.5864 6.63894 12.4936 6.63894 12.4023C6.63894 12.311 6.68453 12.2182 6.77895 12.1238C6.87291 12.0298 6.97003 11.9889 7.07071 11.9981C7.17308 12.0074 7.26137 12.0492 7.33599 12.1238L10.3245 15.1123ZM12.005 21.9875C10.624 21.9875 9.32556 21.7255 8.1096 21.2014C6.89339 20.6773 5.83557 19.966 4.93605 19.0676C4.03654 18.1692 3.32438 17.1128 2.79958 15.8981C2.27488 14.6837 2.0125 13.386 2.0125 12.005C2.0125 10.624 2.27455 9.32556 2.79858 8.1096C3.32273 6.89339 4.034 5.83558 4.93238 4.93607C5.83078 4.03655 6.88725 3.32438 8.10191 2.79958C9.31633 2.27488 10.614 2.0125 11.995 2.0125C13.376 2.0125 14.6744 2.27455 15.8904 2.79858C17.1066 3.32273 18.1644 4.034 19.0639 4.93238C19.9635 5.83078 20.6756 6.88725 21.2004 8.10191C21.7251 9.31633 21.9875 10.614 21.9875 11.995C21.9875 13.376 21.7255 14.6744 21.2014 15.8904C20.6773 17.1066 19.966 18.1644 19.0676 19.064C18.1692 19.9635 17.1128 20.6756 15.8981 21.2004C14.6837 21.7251 13.386 21.9875 12.005 21.9875ZM12 21.2079C14.5703 21.2079 16.7478 20.3158 18.5318 18.5318C20.3158 16.7478 21.2079 14.5703 21.2079 12C21.2079 9.4297 20.3158 7.25218 18.5318 5.46817C16.7478 3.68417 14.5703 2.7921 12 2.7921C9.4297 2.7921 7.25218 3.68417 5.46817 5.46817C3.68417 7.25218 2.7921 9.4297 2.7921 12C2.7921 14.5703 3.68417 16.7478 5.46817 18.5318C7.25218 20.3158 9.4297 21.2079 12 21.2079Z" fill="#C3002F" stroke="#C3002F" stroke-width="0.025"/>
      </svg>`),
    backIcon: (
      <svg width='20' height='20' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4.48588 6L8.0226 9.53672C8.07533 9.58945 8.1017 9.65348 8.1017 9.72881C8.1017 9.80414 8.07533 9.86817 8.0226 9.9209C7.96987 9.97363 7.90584 10 7.83051 10C7.75518 10 7.69115 9.97363 7.63842 9.9209L4.19209 6.47458C4.12429 6.40678 4.07533 6.33145 4.0452 6.24859C4.01507 6.16573 4 6.08286 4 6C4 5.91714 4.01507 5.83428 4.0452 5.75141C4.07533 5.66855 4.12429 5.59322 4.19209 5.52542L7.63842 2.0791C7.69115 2.02637 7.75518 2 7.83051 2C7.90584 2 7.96987 2.02637 8.0226 2.0791C8.07533 2.13183 8.10169 2.19586 8.10169 2.27119C8.10169 2.34652 8.07533 2.41055 8.0226 2.46328L4.48588 6Z'
          fill='#C3002F'
          stroke='#C3002F'
        />
      </svg>
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.primary,
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.primary,
      },
      CoverageItemText: {
        color: variables.colors.text.primary,
      },
    },
    selected: {
      CoverageHeaderName: {
        color: variables.colors.primary.main,
      },
      CoverageHeaderPrice: {
        color: variables.colors.primary.main,
      },
    },
    descriptionCard: {
      backgroundColor: variables.colors.shade3,
      borderColor: variables.colors.shade3,
    },
  },
  CheckoutPage: {
    pageHeader: {
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
    },
    backButton: {
      color: variables.colors.primary.main,
      "&:hover": {
        color: variables.colors.hover,
        background: "none",
      },
      "&:active": {
        color: variables.colors.text.hint,
        background: "none",
      },
    },
  },
  paymentCard: {
    header: {
      //textDecoration: 'underline',
    },
  },
  statusIndicator: {
    borderRadius: 4,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};
export default createTheme(nissan);
